<script setup lang="ts">
import { computed } from 'vue';
import { DgrIcon, DgrLoading } from '@stockmarkteam/donguri-ui';
import type { AdditionalBusinessContextSuggestionStatus } from '@/utils/composables/useAdditionalBusinessContextSuggestion';

const props = defineProps<{
  status: AdditionalBusinessContextSuggestionStatus;
  suggestions: string[];
  canRegenerate: boolean;
}>();

const emit = defineEmits<{
  add: [suggestion: string];
  regenerate: [];
}>();

const description = computed(() =>
  props.status === 'idle'
    ? '入力時に、上の項目をもとに生成します。'
    : '選ぶと入力欄に追加されます。',
);
</script>

<template>
  <section class="additional-business-context-suggestion">
    <div class="header">
      <DgrIcon name="sparkles-fill" size="small" :keep-fill="false" />
      <span class="c-title-bold--m">業務の補足案</span>
      <span
        v-if="status === 'error'"
        class="c-text-regular--s description error"
      >
        補足案を生成できませんでした。
      </span>
      <span v-else class="c-text-regular--s description">
        {{ description }}
      </span>
      <button
        v-if="status !== 'idle'"
        type="button"
        class="c-text-regular--s regenerate-button"
        :disabled="!canRegenerate"
        @click="emit('regenerate')"
      >
        <template v-if="status === 'loading'">
          <DgrLoading size="small" :keep-fill="false" />
          作成中...
        </template>
        <template v-else>
          <DgrIcon name="sync" size="small" />
          {{ status === 'error' ? 'もう一度生成' : '別案を生成' }}
        </template>
      </button>
    </div>
    <div v-if="status === 'loading'" class="suggestions">
      <div class="loading-suggestion"></div>
      <div class="loading-suggestion"></div>
    </div>
    <ul v-else-if="status === 'loaded'" class="suggestions">
      <li
        v-for="suggestion in suggestions"
        :key="suggestion"
        class="suggestion"
      >
        <span class="c-text-regular--m">{{ suggestion }}</span>
        <button
          type="button"
          class="c-title-bold--s add-button"
          @click="emit('add', suggestion)"
        >
          追加
        </button>
      </li>
    </ul>
  </section>
</template>

<style lang="scss" scoped>
.additional-business-context-suggestion {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: $color-gray200;
  border-radius: 4px;
  color: $color-gray1000;

  .header {
    display: flex;
    align-items: center;
    gap: 4px;
    min-height: 23px;

    .c-title-bold--m {
      margin-right: 6px;
    }

    .description {
      color: $color-gray800;

      &.error {
        color: $color-orange1000;
      }
    }

    .regenerate-button {
      display: flex;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;
      height: 23px;
      margin-left: auto;
      padding: 0 4px;
      background: $color-white;
      border: 1px solid $color-gray400;
      border-radius: 3.5px;
      color: $color-gray1000;
      cursor: pointer;

      &:disabled {
        cursor: default;
      }

      .loader.small {
        width: 16px;
        height: 16px;
        color: $color-green600;
      }
    }
  }

  .suggestions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;

    .suggestion {
      display: flex;
      align-items: center;
      gap: 12px;
      box-sizing: border-box;
      min-height: 39px;
      padding: 8px 12px;
      background: $color-white;
      border: 1px solid $color-gray400;
      border-radius: 3.5px;
      word-break: break-word;
    }

    .add-button {
      flex-shrink: 0;
      height: auto;
      margin-left: auto;
      padding: 0;
      background: none;
      border: none;
      color: $color-green600;
      cursor: pointer;
    }

    .loading-suggestion {
      height: 39px;
      background: linear-gradient(
        90deg,
        $color-gray400 0%,
        $color-gray400 40%,
        rgba(255, 255, 255, 0.8) 50%,
        $color-gray400 60%,
        $color-gray400 100%
      );
      background-size: 200% 100%;
      border-radius: 3.5px;
      animation: shimmer 2.5s linear infinite;
    }
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
